import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Yango dispatch status as returned by the Yango API callback lifecycle. */
export type YangoDispatchStatus =
  | "pending"
  | "searching_driver"
  | "driver_assigned"
  | "pickup_arrived"
  | "in_transit"
  | "delivered"
  | "cancelled"
  | "failed";

/** Summary row for the dispatch list. */
export interface YangoDispatch {
  readonly id: string;
  readonly order_id: string;
  readonly order_ref: string;
  readonly customer_name: string;
  readonly customer_phone: string;
  readonly pickup_address: string;
  readonly delivery_address: string;
  readonly status: YangoDispatchStatus;
  readonly cost_fcfa: number | null;
  readonly yango_claim_id: string | null;
  readonly driver_name: string | null;
  readonly driver_phone: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface YangoDispatchListResponse {
  readonly dispatches: readonly YangoDispatch[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
  readonly summary: {
    readonly total_dispatches: number;
    readonly total_cost_fcfa: number;
    readonly pending_count: number;
    readonly in_transit_count: number;
    readonly delivered_count: number;
    readonly failed_count: number;
  };
}

export interface YangoDispatchListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: YangoDispatchStatus | null;
  readonly q?: string | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface YangoDispatchRequest {
  readonly order_id: string;
}

export interface YangoDispatchResponse {
  readonly dispatch: YangoDispatch;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  );
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const yangoApi = {
  /** Fetch paginated list of Yango dispatches. */
  list: (params: YangoDispatchListParams = {}) => {
    const qs = toQueryString({
      page: params.page,
      limit: params.limit,
      status: params.status,
      q: params.q,
      date_from: params.date_from,
      date_to: params.date_to,
    });
    return adminFetch<YangoDispatchListResponse>(
      `/api/admin/deliveries/yango${qs}`,
    );
  },

  /** Dispatch an order to Yango. */
  dispatch: (orderId: string) =>
    adminFetch<YangoDispatchResponse>("/api/admin/deliveries/yango", {
      method: "POST",
      body: JSON.stringify({ order_id: orderId }),
    }),

  /** Cancel a pending Yango dispatch. */
  cancel: (dispatchId: string) =>
    adminFetch<{ success: boolean }>(
      `/api/admin/deliveries/yango/${encodeURIComponent(dispatchId)}/cancel`,
      { method: "POST" },
    ),
} as const;
